import { useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { assertIsNewSitePricingDefinition, getSiteProductDefinition } from '@gitbook/app-pricing';
import { intlPluralizeString } from '@gitbook/react-uikit';
import { Button, Description, Panel, PanelBody } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { BillingInvoicePreview, priceFormatter } from '../../../../../../Components';
import { useSetAnalyticsScreen } from '../../../../../../navigation';
import {
    apiQuery,
    assertOrganizationWithBilling,
    getMonthlyMemberPrice,
    getMonthlySitePrice,
    useCheckoutSite,
    useSiteContext,
} from '../../../../../../state';

// Publish details displayed in the publish dialog shown when a site can not be published immediately.
export function PublishDetails() {
    const { organizationId, site } = useSiteContext();
    const checkout = useCheckoutSite(organizationId, site);
    const { analyticsContext } = useSiteContext();

    const [{ data: organization }, { data: membersUsage }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getMembersUsageForOrganizationById', { organizationId }),
        ],
    });

    const [showBreakdown, setShowBreakdown] = React.useState(false);

    const productDefinition = getSiteProductDefinition(site.type);
    const checkoutResponse = checkout.state.type;
    const checkoutInvoice = checkoutResponse === 'checkout' ? checkout.state.invoice : null;

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: analyticsContext.site, organization: analyticsContext.organization },
            {
                screen: 'site_publish_modal',
                publishType: checkoutResponse,
            }
        ),
        [analyticsContext, checkoutResponse]
    );

    const toggleBreakdown = () => {
        setShowBreakdown(!showBreakdown);
        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organizationId },
                { type: 'site_publish_view_breakdown' }
            )
        );
    };

    // The user has permission to publish a site, so they should have access to billing information.
    assertOrganizationWithBilling(organization);
    const isOnYearlyPlan = organization.billing.interval === api.BillingInterval.Yearly;
    assertIsNewSitePricingDefinition(productDefinition);
    const monthlyPrice = getMonthlySitePrice({
        siteType: productDefinition.key,
        billing: organization.billing,
    });

    // Calculate member pricing
    const memberCount = membersUsage?.members?.total || 0;
    const monthlyMemberPrice = getMonthlyMemberPrice({
        product: api.BillingProduct.Pro2024,
        billing: organization.billing,
    });
    const monthlyMemberCost = monthlyMemberPrice ? monthlyMemberPrice * memberCount : 0;
    const totalMonthlyCost = monthlyPrice + monthlyMemberCost;

    return (
        <div className="flex flex-col gap-2">
            <Description>
                Your {productDefinition.title.toLowerCase()} site is ready to shine. Once published,
                you'll be able to share it with the world.
            </Description>

            <Description>
                {(() => {
                    switch (checkoutResponse) {
                        case 'trial-available':
                            return 'Your free trial will start automatically - no credit card required.';

                        case 'extended-trial-available':
                            return 'Restart your GitBook trial for another 7 days - no credit card required.';

                        case 'go-ahead':
                            return `Your site needs to be added to your subscription
                                before it can go live. Your subscription will increase by ${priceFormatter.format(monthlyPrice)} per month.`;

                        case 'checkout':
                            return `Your site requires a subscription before it can go live for a total of ${priceFormatter.format(totalMonthlyCost)} per month, with ${priceFormatter.format(monthlyPrice)} for the ${productDefinition.title} plan and ${priceFormatter.format(monthlyMemberCost)} for ${memberCount} ${intlPluralizeString(
                                {
                                    count: memberCount,
                                    singular: 'member',
                                    plural: 'members',
                                }
                            )}.`;
                        default:
                            assertNever(checkoutResponse);
                    }
                })()}
            </Description>

            {checkoutInvoice && checkoutInvoice.lines.length > 0 && isOnYearlyPlan ? (
                <Description>
                    Because you are on the annual plan, you will be charged{' '}
                    {priceFormatter.format(checkoutInvoice.amountDueToday / 100)} today. For more
                    details,{' '}
                    <Button
                        kind="link"
                        className="ui-small h-4"
                        label="view a breakdown"
                        onPress={toggleBreakdown}
                    />{' '}
                    of your upcoming invoice.
                </Description>
            ) : null}

            {checkoutInvoice && checkoutInvoice.lines.length > 0 && !isOnYearlyPlan ? (
                <Description>
                    For more details,{' '}
                    <Button
                        kind="link"
                        className="ui-small h-4"
                        label="view a breakdown"
                        onPress={toggleBreakdown}
                    />{' '}
                    of your upcoming invoice.
                </Description>
            ) : null}

            {checkoutInvoice && showBreakdown ? (
                <Panel>
                    <PanelBody>
                        <BillingInvoicePreview
                            billingInterval={organization.billing.interval}
                            billingPeriodEnd={organization.billing.endDate}
                            invoice={checkoutInvoice}
                        />
                    </PanelBody>
                </Panel>
            ) : null}
        </div>
    );
}
